import { MotionConfig } from "motion/react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

const STORAGE_KEY = "ma-soi:visual-effects";
const MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

const VisualEffectsContext = createContext({
  reduced: false,
  systemReduced: false,
  toggle: () => {},
  backdrop: "night" as "night" | "dawn",
  setBackdrop: (_value: "night" | "dawn") => {},
});

export function VisualEffectsProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "reduced"; } catch { return false; }
  });
  const [systemReduced, setSystemReduced] = useState(() => window.matchMedia(MEDIA_QUERY).matches);
  const reduced = preference || systemReduced;
  const [backdrop, setBackdrop] = useState<"night" | "dawn">("night");

  useEffect(() => {
    const media = window.matchMedia(MEDIA_QUERY);
    const update = () => setSystemReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    // Cài đặt áp dụng cả dialog ở top layer và chuyển động CSS trong ứng dụng.
    document.documentElement.dataset.effects = reduced ? "reduced" : "full";
    return () => { delete document.documentElement.dataset.effects; };
  }, [reduced]);

  useEffect(() => {
    const updateVisibility = () => {
      document.documentElement.dataset.pageHidden = String(document.hidden);
    };
    updateVisibility();
    document.addEventListener("visibilitychange", updateVisibility);
    return () => {
      document.removeEventListener("visibilitychange", updateVisibility);
      delete document.documentElement.dataset.pageHidden;
    };
  }, []);

  function toggle() {
    // Ưu tiên hệ điều hành; nút vẫn có thể focus để đọc giải thích.
    if (systemReduced) return;
    const next = !preference;
    setPreference(next);
    try { localStorage.setItem(STORAGE_KEY, next ? "reduced" : "full"); } catch {}
  }

  return (
    <VisualEffectsContext.Provider value={{ reduced, systemReduced, toggle, backdrop, setBackdrop }}>
      <MotionConfig reducedMotion={reduced ? "always" : "user"}>{children}</MotionConfig>
    </VisualEffectsContext.Provider>
  );
}

export function useVisualEffects() { return useContext(VisualEffectsContext); }
